import { liteClient } from 'algoliasearch/lite';
import React, { useState, useEffect } from 'react';
import t from '../utils/i18n.js';
import getResults from './getResults.js';

const GlobalSearch = ({id, token, lang, pageLimit}) => {
    pageLimit = parseInt(pageLimit);
    const client = liteClient(id, token);

    const [results, setResults] = useState([]);
    const [resultCount, setResultCount] = useState(null);
    const [query, setQuery] = useState('');
    const [searchType, setSearchType] = useState('all');
    const [isLoading, setIsLoading] = useState(false);
    const [totalResults, setTotalResults] = useState([]);
    const [currentOffset, setCurrentOffset] = useState(0);

    // options for radio button filters
    const searchTypes = {
        'all': 'Alt innhold',
        'blog': 'Artikler',
        'school': 'Skoler',
        'course': 'Linjer'
    };

    // names shown in result items
    const typeNames = {
        blog: 'Artikkel',
        school: 'Skole',
        course: 'Linje ved',
        extracourse: 'Valgfag ved',
        schooltrip: 'Skoletur ved'
    };

    const changeSearchType = (type) => {
        if (type === searchType) {
            return;
        }
        setSearchType(type);
        search(false, type);
    };

    const showMore = () => {
        setResults(totalResults.slice(0, currentOffset + (pageLimit*2)));
        setCurrentOffset(currentOffset + pageLimit);
    };

    const search = async (preserveState, queryType, queryString) => {
        if (!queryType) {
            queryType = searchType;
        }
        if (!queryString) {
            queryString = query;
        }
        setResults([]);
        setResultCount(null);
        if (!preserveState) {
            history.pushState({
                    query: queryString,
                    searchType: queryType
                },
                '',
                window.location.pathname + '?q=' + queryString + '&t=' + queryType
            );
        }
        if (!queryString) {
            setResults([]);
            setResultCount(null);
            return;
        }
        setIsLoading(true);
        setCurrentOffset(0);

        FHS.track("Nettstedssøk", queryString);
        const result = await getResults(client, lang, queryString, queryType);

        setIsLoading(false);
        setTotalResults(result.result);

        if (result.result.length > pageLimit) {
            result.result = result.result.slice(0, pageLimit);
        }

        setResults(result.result);
        setResultCount(result.totalHits);

    };

    // popstate handler
    const onPop = (e) => {
        let triggerSearch = false;
        if (e.state.searchType && searchTypes[e.state.searchType] && e.state.searchType !== searchType) {
            setSearchType(e.state.searchType);
            triggerSearch = true;
        }
        if (e.state.query && e.state.query !== query) {
            setQuery(e.state.query);
            triggerSearch = true;
        }
        if (triggerSearch) {
            search(true);
        }
    };

    // add popstate listener to window
    useEffect(() => {
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, [onPop]);

    // get parameters from url on initialization
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryString = urlParams.get('q');
        let queryType = urlParams.get('t');

        if (queryType && searchTypes[queryType]) {
            setSearchType(queryType);
        } else {
            queryType = 'all';
        }

        if (queryString) {
            setQuery(queryString);
            search(true, queryType, queryString);
        }
    }, []);

    return (
        <div>
            <form onSubmit={(e) => { e.preventDefault(); search()}}>
                <label className="search__label visuallyhidden" for="main-search-input">{ t('Søk', lang) }</label>
                <input name="q" type="text" id="main-search-input" value={query} className="search__result-input" placeholder={ t('Søk', lang) + '...'} autofocus onInput={(e) => setQuery(e.target.value)} />
                <button type="submit" class="visuallyhidden" tabindex="-1">{ t('Søk', lang) }</button>
                <fieldset>
                    <legend>{ t('Velg type innhold', lang) }</legend>
                    <div class="form__options form__options--radio form__options--radio-row search__type-options" data-coursetypes>
                        {Object.keys(searchTypes).map((type) => {
                            return (<div>
                                <label class="radio radio--small">
                                    <input type="radio" name="t" value={type} checked={type == searchType} onClick={(e) => changeSearchType(e.target.value)} />
                                    <i class="radio__indicator"></i>{ t(searchTypes[type], lang) }
                                </label>
                            </div>)
                        })}
                    </div>
                </fieldset>
                {resultCount !== null ? (
                        <p className="search__result-count">{ t('Ditt søk ga', lang) } <strong>{resultCount}</strong> { t('treff', lang) }:</p>
                    ) : ''
                }
            </form>
            <div className={'search__loader' + (isLoading ? ' active' : '')}>
                <div className="spinner">
                    <span className="spinner-inner-1"></span>
                    <span className="spinner-inner-2"></span>
                    <span className="spinner-inner-3"></span>
                </div>
            </div>
            <ul className="search__result">
                {results.map((item) => {
                    let typeName = t(typeNames[item.type] ?? '', lang);

                    if (item.school && (item.type === 'course' || item.type === 'extracourse' || item.type === 'schooltrip')) {
                        typeName += ' ' + item.school;
                    }

                    return (
                    <li className="search__result-item" key={item.objectID}>
                        <a href={ item.url }>
                            <div class="search__result-item--type">{ typeName }</div>
                            <h2 dangerouslySetInnerHTML={{ __html: item.title }} />
                            <p dangerouslySetInnerHTML={{ __html: item.teaser }} />
                        </a>
                    </li>
                )})}
            </ul>
            {totalResults.length > (currentOffset + pageLimit) ? (
                <button className="button button--primary button--loadmore" onClick={(e) => showMore()}>{ t('Vis flere', lang) }</button>
            ) : ''}
        </div>
    );
};

export default GlobalSearch;
